import React, {useCallback, useState} from "react";
import {Typography} from "@mui/material";
import {useQueryClient} from "@tanstack/react-query";

import {useMutationWithAccessToken, useQueryWithAccessToken} from "../../../../hooks/TanstackHooks";
import {useComponentForQueryResult} from "../../../../hooks/UseComponentForQueryResult";
import {cacheKeys} from "../../../../services/shared/serviceConstants";
import {
  getClientNotificationSettings,
  updateClientNotificationSettings
} from "../../../../services/Clients/ClientNotificationSettingsQueries";
import {useErrorDialog} from "../../../../components/common/Dialog/ErrorDialog";
import {IClientNotificationSettings} from "../../../../types/ClientNotificationSettings";
import {NotificationSettingsForm} from "./NotificationSettingsForm";

// Every cached copy of a client's notification settings, whichever client was
// current when it was fetched.
const isNotificationSettingsQuery = (query: { queryKey: readonly unknown[] }) =>
  String(query.queryKey[0]).startsWith(cacheKeys.clientNotificationSettings);

// Loads, shows and saves the notification settings of a client: clientId if
// given, otherwise the current client.
export const ClientNotificationSettings = ({
  clientId,
  userRole,
  canEdit,
  onSaved,
  onCancel,
}: {
  clientId?: string,
  userRole: string,
  canEdit: boolean,
  onSaved?: () => void,
  onCancel?: () => void,
}) => {
  const queryClient = useQueryClient();
  const [userErrorMessage, setUserErrorMessage] = useState('');

  const queryResult = useQueryWithAccessToken({
    queryKey: [cacheKeys.clientNotificationSettings, clientId ?? 'current'],
    queryFunction: getClientNotificationSettings,
    queryFunctionVariables: {clientId},
    tanstackOptions: {
      onError: () => {
        setUserErrorMessage('error accessing the notification settings');
      }
    },
    userRole,
  });

  const {
    dialog: errorDialog,
    setIsOpen: setIsErrorDialogOpen,
    setErrorMessage,
  } = useErrorDialog({
    onDismiss: () => {},
  });

  const {
    mutate: doUpdate,
    isPending: isSaving,
  } = useMutationWithAccessToken({
    mutation: updateClientNotificationSettings,
    tanstackOptions: {
      onSuccess: () => {
        queryClient.invalidateQueries({predicate: isNotificationSettingsQuery});
        onSaved?.();
      },
      onError: (error: { response?: { errors?: { message: string }[] } }) => {
        setErrorMessage(error.response?.errors?.[0]?.message ?? 'Could not save the notification settings');
        setIsErrorDialogOpen(true);
      }
    }
  });

  const onSave = useCallback((settings: IClientNotificationSettings) => {
    doUpdate({settings, role: userRole});
  }, [doUpdate, userRole]);

  const pageLoadFunction = useCallback(
    () => <>
      {!canEdit && <Typography sx={{fontStyle: 'italic', marginBottom: 2}}>
        Only an account admin of {queryResult.data?.clientName} can change these settings.
      </Typography>}
      <NotificationSettingsForm
        settings={queryResult.data}
        canEdit={canEdit}
        isSaving={isSaving}
        onSave={onSave}
        onCancel={onCancel}
      />
    </>,
    [queryResult.data, canEdit, isSaving, onSave, onCancel]
  );

  return <>
    {errorDialog}
    {useComponentForQueryResult({
      queryResult,
      pageLoadFunction,
      userErrorMessage,
      errorMessageDoesIndicateError: true,
    })}
  </>;
};
